import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import { useTwoFactorSetup, useTwoFactorVerify } from '../hooks/useLogin.js';
import Button from '../components/Button.jsx';
import FormField from '../components/FormField.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';

export default function TwoFactorSetupPage({ preauthToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = preauthToken ?? location.state?.preauth_token;

  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState('');
  const [step, setStep] = useState('loading'); // loading | scan | verify
  const setupMutation = useTwoFactorSetup();
  const verifyMutation = useTwoFactorVerify();
  const codeRef = useRef(null);

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    setupMutation.mutateAsync({ preauth_token: token }).then(async (data) => {
      const url = data?.otpauth_url;
      const sec = data?.secret;
      if (url) {
        const dataUrl = await QRCode.toDataURL(url, { margin: 1, width: 200 });
        setQrDataUrl(dataUrl);
        setSecret(sec ?? null);
        setStep('scan');
      } else {
        setStep('verify');
      }
    }).catch(() => {
      navigate('/login', { replace: true });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (step === 'verify') codeRef.current?.focus();
  }, [step]);

  async function handleVerify(e) {
    e.preventDefault();
    try {
      await verifyMutation.mutateAsync({ preauth_token: token, code });
      navigate('/', { replace: true });
    } catch (err) {
      // error shown via verifyMutation.error
    }
  }

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-slate-900">Activation 2FA</h1>
          <p className="text-sm text-slate-500 mt-1">
            Scannez ce QR code avec Google Authenticator ou Authy.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5">
          {step === 'scan' && qrDataUrl && (
            <>
              <div className="flex justify-center">
                <img
                  src={qrDataUrl}
                  alt="QR code 2FA"
                  className="w-48 h-48 rounded-lg border border-slate-100"
                />
              </div>
              {secret && (
                <div className="text-center">
                  <p className="text-xs text-slate-500 mb-1">
                    Ou saisissez le code manuellement :
                  </p>
                  <p className="text-xs font-mono bg-slate-50 border border-slate-200 rounded px-3 py-2 select-all break-all text-slate-700">
                    {secret}
                  </p>
                </div>
              )}
              <Button
                className="w-full"
                onClick={() => setStep('verify')}
              >
                J'ai scanné le QR code
              </Button>
            </>
          )}

          {step === 'verify' && (
            <form onSubmit={handleVerify} className="space-y-4">
              {verifyMutation.error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
                  {verifyMutation.error.message ?? 'Code invalide'}
                </div>
              )}
              <FormField label="Code à 6 chiffres" htmlFor="totp-code" required>
                <input
                  id="totp-code"
                  ref={codeRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-center tracking-widest font-mono focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                  placeholder="000000"
                  required
                />
              </FormField>
              <Button
                type="submit"
                className="w-full"
                loading={verifyMutation.isPending}
                disabled={code.length < 6}
              >
                Valider et accéder au dashboard
              </Button>
              {qrDataUrl && (
                <button
                  type="button"
                  className="w-full text-sm text-slate-500 hover:text-slate-700 underline"
                  onClick={() => setStep('scan')}
                >
                  Revoir le QR code
                </button>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
